import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter
} from "@/components/ui/card";
import { QuoteFormValues } from "@/schemas/quoteSchema";
import { useSimpleQuoteContext } from "@/contexts/SimpleQuoteContext";
import { X } from "lucide-react";
import { Quote } from "@/types/quote";

interface InlineQuoteFormProps {
  quote?: Quote | null;
  onCancel: () => void;
}

export function InlineQuoteForm({ quote, onCancel }: InlineQuoteFormProps) {
  const {
    categories,
    handleQuoteUpdate,
    handleQuoteCreate,
    editMode
  } = useSimpleQuoteContext();

  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [source, setSource] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showError, setShowError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Add animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Populate fields when editing
  useEffect(() => {
    if (quote) {
      setContent(quote.content || "");
      setAuthor(quote.author || "");
      setSource(quote.source || "");
      setSelectedCategories(
        quote.categories?.map(cat => cat.id) || []
      );
    } else {
      setContent("");
      setAuthor("");
      setSource("");
      setSelectedCategories([]);
    }
    setShowError(false);
  }, [quote]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim()) {
      setShowError(true);
      return;
    }

    setIsSaving(true);
    try {
      const formData = {
        content: content.trim(),
        author: author.trim() || '',
        source: source.trim() || '',
        categories: selectedCategories.map(id => {
          const category = categories.find(c => c.id === id);
          return { id, name: category?.name || '' };
        })
      };

      if (editMode === 'edit' && quote) {
        await handleQuoteUpdate({
          ...quote,
          ...formData
        });
      } else {
        await handleQuoteCreate(formData);
      }
      onCancel();
    } catch (error) {
      console.error('Failed to save quote:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const categoryOptions = React.useMemo(() => 
    categories.map(category => ({
      value: category.id,
      label: category.name,
    })),
    [categories]
  );

  const formTitle = editMode === 'edit' ? "Edit Quote" : "Add New Quote";

  return (
    <div 
      className={`transition-all duration-500 ease-in-out transform ${
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
      }`}
    >
      <Card className="border-0 shadow-none bg-transparent">
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="content">Quote Content</Label>
            <Textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter the quote text"
              required
              rows={4}
              disabled={isSaving}
            />
            {showError && (
              <p className="text-sm text-red-500">Content is required</p>
            )}
          </div>

          <div className="space-y-2 inline-block w-1/2 px-2-0">
            <Label htmlFor="author">Author</Label>
            <Input
              id="author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Who said or wrote this quote"
              disabled={isSaving}
            />
          </div>

          <div className="space-y-2 inline-block w-1/2 px-2">
            <Label htmlFor="source">Source</Label>
            <Input
              id="source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="Book, speech, article, etc."
              disabled={isSaving}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="categories">Categories</Label>
            <MultiSelect
              options={categoryOptions}
              selected={selectedCategories}
              onChange={setSelectedCategories}
              placeholder="Select categories"
              className="w-full"
              disabled={isSaving}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : editMode === 'edit' ? 'Save Changes' : 'Add Quote'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
